/**
 * `map` template — the account-geography dot map, drawn as plain JSX rather than reusing the
 * field's imperative `drawMap` (`sku-velocity/field/lenses.ts`): that builds DETACHED SVG via
 * direct DOM writes, so it can't carry a React hover handler on each account bubble. Reuses the
 * field's own pure geometry instead — `mapFrame` (the coastline/world mask), `projection` (the
 * equirectangular lon/lat → pixel transform) and `accountsFor` (the bubble set + radii) — so the
 * same frame, mask and bubble scale `/velocity`'s Customer widget draws is what this renders too.
 *
 * Selection = the spec's `cohort` evaluated against the whole snapshot; absent a cohort,
 * `accountsFor(null)` returns the whole-site bubble set (every served market) with no red.
 */

import { useMemo, useState } from "react";

import {
  type AccountDot,
  accountsFor,
  mapFrame,
  medianAccounts,
  projection,
} from "@/features/sku-velocity/field/lenses";
import type { MapView } from "@/lib/api";
import { GEO_MARKETS, type Sku } from "@/lib/data";
import { evalCohort } from "@/lib/specs/evaluate";

import { ChartTooltip } from "./chart-tooltip";
import { useChartSize } from "./use-chart-size";
import type { ViewChrome, ViewSize } from "./view-shell";

interface MapChartProps {
  spec: MapView;
  size: ViewSize;
  skus: readonly Sku[];
  chrome?: ViewChrome;
}

export function MapChart({ spec, skus, chrome = "full" }: MapChartProps) {
  const box = useChartSize();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const sel = useMemo(
    () => (spec.cohort ? evalCohort(spec.cohort, skus) : null),
    [spec.cohort, skus],
  );
  const accs = useMemo(() => accountsFor(sel), [sel]);

  const w = box.width;
  const h = box.height;
  const frame = useMemo(() => mapFrame(), []);
  const proj = useMemo(
    () => (w > 0 && h > 0 ? projection(w, h, frame.box, frame.fit) : null),
    [w, h, frame],
  );

  // The background land-mask dot grid — same density `drawMap` uses (~4.7px cells).
  const gridDots = useMemo(() => {
    if (!proj || w === 0 || h === 0) {
      return [];
    }
    const cols = Math.round(w / 4.7);
    const rows = Math.round(h / 4.7);
    const dx = w / cols;
    const dy = h / rows;
    const rr = Math.min(dx, dy) * 0.34;
    const dots: { x: number; y: number; r: number }[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cx = c * dx + dx / 2;
        const cy = r * dy + dy / 2;
        const [lon, lat] = proj.from(cx, cy);
        if (frame.isLand(lon, lat)) {
          dots.push({ x: cx, y: cy, r: rr });
        }
      }
    }
    return dots;
  }, [proj, w, h, frame]);

  const bubbles: (AccountDot & { x: number; y: number })[] = useMemo(() => {
    if (!proj) {
      return [];
    }
    return accs.flatMap((a) => {
      const [x, y] = proj.to(a.lon, a.lat);
      return x < -4 || x > w + 4 ? [] : [{ ...a, x, y }];
    });
  }, [proj, accs, w]);

  const hovered = hoverIdx !== null ? bubbles[hoverIdx] : undefined;

  return (
    <div className="relative flex h-full min-h-[90px] min-w-0 flex-col gap-1">
      <div ref={box.ref} className="relative min-h-0 min-w-0 flex-1">
        {w > 0 && h > 0 ? (
          <svg width={w} height={h}>
            {gridDots.map((d, i) => (
              <circle key={i} cx={d.x} cy={d.y} r={d.r} fill="#E5E2DE" />
            ))}
            {bubbles.map((b, i) => (
              <circle
                key={b.name ?? i}
                cx={b.x}
                cy={b.y}
                r={b.r}
                fill="#EB1700"
                fillOpacity={b.name === hovered?.name ? 1 : 0.85}
                stroke={b.name === hovered?.name ? "#141414" : "none"}
                strokeWidth={1}
                tabIndex={0}
                role="img"
                aria-label={`${b.name ?? "market"}: ${Math.round(b.weight ?? 0).toLocaleString()} accounts`}
                className="cursor-default outline-none"
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                onFocus={() => setHoverIdx(i)}
                onBlur={() => setHoverIdx((cur) => (cur === i ? null : cur))}
              />
            ))}
          </svg>
        ) : null}
      </div>
      {hovered ? (
        <ChartTooltip x={`${(hovered.x / w) * 100}%`} y={`${(hovered.y / h) * 100}%`}>
          <div className="font-semibold">{hovered.name}</div>
          <div>
            {Math.round(hovered.weight ?? 0).toLocaleString()} accounts
            {sel ? " in the selection" : null}
          </div>
          {/* Per-SKU account weights can overlap and double-count (`medianAccounts`'s own
              caveat), so the selection's figure can exceed the market's site-wide one — never
              worded as a fraction of it. */}
          {sel ? (
            <div className="opacity-75">
              {Math.round(hovered.siteWeight ?? 0).toLocaleString()} accounts site-wide
            </div>
          ) : null}
        </ChartTooltip>
      ) : null}
      {chrome === "bare" ? null : (
        <p className="text-muted-foreground shrink-0 text-[11px]">
          {sel
            ? (() => {
                const m = medianAccounts(sel);
                return m > 0
                  ? `${m.toLocaleString()} accounts / SKU, median — accounts overlap between SKUs, so this is per SKU, not a total`
                  : "0 accounts / SKU — over half ship to no accounts";
              })()
            : `Every account this site serves, across ${GEO_MARKETS.length} markets`}
        </p>
      )}
    </div>
  );
}
